import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Tooltip,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon, 
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { db, kbStorage, auth } from '../../firebase';
import { collection, getDocs, orderBy, query, addDoc, updateDoc, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { ref, listAll } from 'firebase/storage';

const INFORMED_FISHING_API_BASE = 'https://us-central1-bsi-bass.cloudfunctions.net/informedFishingAIApi';

const DEFAULT_TEST_METADATA = {
  "biometricsNextDateTime": "2026-04-21T17:00:00.000Z",
  "uid": "Y2YnlyLjI5YC6xxw97CivDpUuMz2",
  "userLevel": "Beginner",
  "stationId": "ADM",
  "localTimeZone": "America/Chicago",
  "moonSet": "2026-04-21T05:18:42Z",
  "lakeProfileId": "RF",
  "biometricsNowDateTime": "2026-04-21T14:45:00.000Z",
  "waterTemp": -1,
  "localTimeZoneOffset": "-0500",
  "moonOverhead": "2026-04-21T22:19:33Z",
  "email": "muskymetrics@gmail.comm",
  "biometricsLater": 53.5,
  "moonRise": "2026-04-21T14:49:33Z",
  "fishingDate": "4/21/2026, 8:02:23 AM",
  "lake": "Lake of the Arbuckles",
  "biometricsNow": 64.5,
  "biometricsPattern": "Normal",
  "lakeProfileName": "Flatland Reservoir",
  "biometricsInfluencers": {"sun": 0, "pressure": 0.69, "wind": 0, "humidity": 0, "moon": 0.31, "other": 0},
  "lakeType": "Flatland Reservoir",
  "randomVariables": [0, 1, 0, 2, 2, 1, 1, 6, 2, 8, 4, 10, 10, 3, 6, 8, 13, 2, 12, 6],
  "moonPercent": "16%",
  "isAnonymous": false,
  "version": "1.7.6",
  "biometricsScore": 41,
  "moonUnderfoot": "2026-04-21T10:03:42+00:00",
  "modelId": "gemini-2.5-flash-lite",
  "sessionId": "87431b1d-428c-4bce-9f56-8626991f1b36",
  "lakeLat": 34.4361112978787,
  "biometricsNext": 41,
  "displayName": "Jeff Lang",
  "moonPhase": "WaxingCrescent",
  "biometricsLaterDateTime": "2026-04-21T21:45:00.000Z",
  "providerId": "firebase",
  "timeOfYear": "Spawn",
  "buildNumber": "898",
  "lakeLng": -97.01995236799121,
  "phoneNumber": null,
  "lakeMap": "<Map latitude='34.4361112978787' longitude='-97.01995236799121' latitudeDelta='0.015' longitudeDelta='0.015'/>"
};

function LLMPrompts() {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    panelId: '',
    modelId: ''
  });
  const [kbFolders, setKbFolders] = useState([]);
  const [kbFoldersLoading, setKbFoldersLoading] = useState(false);
  const [testMetadata, setTestMetadata] = useState(JSON.stringify(DEFAULT_TEST_METADATA, null, 2));
  const [testMetadataError, setTestMetadataError] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  const [formData, setFormData] = useState({
    _id: '',
    name: '',
    panelId: '',
    modelId: '',
    systemInstructions: '',
    prompt: '',
    promptBeginner: '',
    promptIntermediate: '',
    mapLocations: '',
    knowledgeBasePath: '',
    vectorSearchQuery: '',
    sourceType: 'knowledgebase',
    config: {
      topP: 0.8,
      topK: 10,
      tokens: 10000,
      temp: 0.7
    }
  });

  const fetchKbFolders = async () => {
    setKbFoldersLoading(true);
    try {
      const rootRef = ref(kbStorage, '');
      const result = await listAll(rootRef);
      setKbFolders(result.prefixes.map(p => p.name));
    } catch (error) {
      console.error('Error fetching knowledge base folders:', error);
      setSnackbar({ open: true, message: 'Error fetching knowledge base folders: ' + error.message, severity: 'error' });
      setKbFolders([]);
    } finally {
      setKbFoldersLoading(false);
    }
  };

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const promptsRef = collection(db, 'llm-panel-prompts');
      const q = query(promptsRef, orderBy('name', 'asc'));
      
      const querySnapshot = await getDocs(q);
      const promptsData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        promptsData.push({
          id: doc.id,
          ...data
        });
      });

      setPrompts(promptsData);
    } catch (error) {
      console.error('Error fetching prompts:', error);
      setSnackbar({ open: true, message: 'Error fetching prompts: ' + error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (promptId) => {
    setExpandedRow(expandedRow === promptId ? null : promptId);
  };

  const handleOpenDialog = (prompt = null) => {
    if (prompt) {
      setEditingPrompt(prompt);
      setFormData({
        _id: prompt._id || prompt.id || '',
        name: prompt.name || '',
        panelId: prompt.panelId || '',
        modelId: prompt.modelId || '',
        systemInstructions: prompt.systemInstructions || '',
        prompt: prompt.prompt || '',
        promptBeginner: prompt.promptBeginner || '',
        promptIntermediate: prompt.promptIntermediate || '',
        mapLocations: prompt.mapLocations || '',
        knowledgeBasePath: prompt.knowledgeBasePath || '',
        vectorSearchQuery: prompt.vectorSearchQuery || '',
        sourceType: prompt.sourceType || 'knowledgebase',
        config: {
          topP: prompt.config?.topP ?? 0.8,
          topK: prompt.config?.topK ?? 10,
          tokens: prompt.config?.tokens ?? 10000,
          temp: prompt.config?.temp ?? 0.7
        }
      });
    } else {
      setEditingPrompt(null);
      setFormData({
        _id: '',
        name: '',
        panelId: '',
        modelId: '',
        systemInstructions: '',
        prompt: '',
        promptBeginner: '',
        promptIntermediate: '',
        mapLocations: '',
        knowledgeBasePath: '',
        vectorSearchQuery: '',
        sourceType: 'knowledgebase',
        config: {
          topP: 0.8,
          topK: 10,
          tokens: 10000,
          temp: 0.7
        }
      });
    }
    fetchKbFolders();
    setTestMetadata(JSON.stringify(DEFAULT_TEST_METADATA, null, 2));
    setTestMetadataError(false);
    setTestResult(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPrompt(null);
    setTestResult(null);
  };

  const handleFormChange = (field, value) => {
    if (field.startsWith('config.')) {
      const configField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        config: {
          ...prev.config,
          [configField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const validateTestMetadata = () => {
    try {
      JSON.parse(testMetadata);
      setTestMetadataError(false);
      return true;
    } catch (e) {
      setTestMetadataError(true);
      return false;
    }
  };

  const handleTestPrompt = async () => {
    if (!validateTestMetadata()) return;
    if (!auth.currentUser) {
      setTestResult({ error: 'Session expired. Please log in again.' });
      return;
    }
    setTestLoading(true);
    setTestResult(null);
    try {
      const token = await auth.currentUser.getIdToken(true);
      const metadata = JSON.parse(testMetadata);
      const panelId = formData.panelId;
      const response = await fetch(
        `${INFORMED_FISHING_API_BASE}/llm/panel/${panelId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(metadata),
        }
      );
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      let transactionDetails = null;
      if (data.transactionId) {
        try {
          const txnSnap = await getDoc(doc(db, 'user-subscription-transactions', data.transactionId));
          if (txnSnap.exists()) {
            transactionDetails = txnSnap.data();
          } else {
            console.warn(`Transaction doc ${data.transactionId} not found in Firestore`);
          }
        } catch (fetchErr) {
          console.error('Failed to fetch transaction details:', fetchErr.message);
        }
      }
      setTestResult({ ...data, transactionDetails });
    } catch (error) {
      console.error('Test prompt failed:', error);
      setTestResult({ error: error.message });
    } finally {
      setTestLoading(false);
    }
  };

  const handleSavePrompt = async () => {
    try {
      const promptData = {
        _id: formData._id,
        name: formData.name,
        panelId: formData.panelId,
        modelId: formData.modelId,
        systemInstructions: formData.systemInstructions,
        prompt: formData.prompt,
        promptBeginner: formData.promptBeginner,
        promptIntermediate: formData.promptIntermediate,
        mapLocations: formData.mapLocations,
        knowledgeBasePath: formData.knowledgeBasePath,
        vectorSearchQuery: formData.vectorSearchQuery,
        sourceType: formData.sourceType,
        config: {
          topP: parseFloat(formData.config.topP) || 0.8,
          topK: parseInt(formData.config.topK) || 10,
          tokens: parseInt(formData.config.tokens) || 10000,
          temp: parseFloat(formData.config.temp) || 0.7
        }
      };

      if (editingPrompt) {
        // Update existing prompt
        const promptRef = doc(db, 'llm-panel-prompts', editingPrompt.id);
        await updateDoc(promptRef, promptData);
        setSnackbar({ open: true, message: 'Prompt updated successfully', severity: 'success' });
      } else {
        // Create new prompt
        await addDoc(collection(db, 'llm-panel-prompts'), promptData);
        setSnackbar({ open: true, message: 'Prompt created successfully', severity: 'success' });
      }

      handleCloseDialog();
      fetchPrompts();
    } catch (error) {
      console.error('Error saving prompt:', error);
      setSnackbar({ open: true, message: 'Error saving prompt: ' + error.message, severity: 'error' });
    }
  };

  const handleDeleteClick = (prompt, event) => {
    event.stopPropagation();
    setPromptToDelete(prompt);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!promptToDelete) return;

    try {
      await deleteDoc(doc(db, 'llm-panel-prompts', promptToDelete.id));
      setSnackbar({ open: true, message: 'Prompt deleted successfully', severity: 'success' });
      setDeleteConfirmOpen(false);
      setPromptToDelete(null);
      fetchPrompts();
    } catch (error) {
      console.error('Error deleting prompt:', error);
      setSnackbar({ open: true, message: 'Error deleting prompt: ' + error.message, severity: 'error' });
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setPromptToDelete(null);
  };

  const handleRequestSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (column, value) => {
    setFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const sortedPrompts = React.useMemo(() => {
    // First filter the prompts
    let filtered = prompts.filter(prompt => {
      return (
        (filters.name === '' || (prompt.name || '').toLowerCase().includes(filters.name.toLowerCase())) &&
        (filters.panelId === '' || (prompt.panelId || '').toLowerCase().includes(filters.panelId.toLowerCase())) &&
        (filters.modelId === '' || (prompt.modelId || '').toLowerCase().includes(filters.modelId.toLowerCase()))
      );
    });

    // Then sort the filtered results
    const sorted = [...filtered];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle undefined/null values
        if (aVal === undefined || aVal === null) aVal = '';
        if (bVal === undefined || bVal === null) bVal = '';

        // Handle string comparison
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        // Handle numeric comparison
        if (sortConfig.direction === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      });
    }
    return sorted;
  }, [prompts, sortConfig, filters]);

  useEffect(() => {
    fetchPrompts();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          LLM Prompts Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Prompt
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell width="50">
                  <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
                    <IconButton
                      size="small"
                      onClick={() => setShowFilters(!showFilters)}
                      color={showFilters ? "primary" : "default"}
                    >
                      <FilterListIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'name'}
                    direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'panelId'}
                    direction={sortConfig.key === 'panelId' ? sortConfig.direction : 'asc'}
                    onClick={() => handleRequestSort('panelId')}
                  >
                    Panel ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'modelId'}
                    direction={sortConfig.key === 'modelId' ? sortConfig.direction : 'asc'}
                    onClick={() => handleRequestSort('modelId')}
                  >
                    Model ID
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" width="120">Actions</TableCell>
              </TableRow>
              {showFilters && (
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="Filter by name..."
                      value={filters.name}
                      onChange={(e) => handleFilterChange('name', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      variant="outlined"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="Filter by panel ID..."
                      value={filters.panelId}
                      onChange={(e) => handleFilterChange('panelId', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      variant="outlined"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="Filter by model ID..."
                      value={filters.modelId}
                      onChange={(e) => handleFilterChange('modelId', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      variant="outlined"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {sortedPrompts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body1" sx={{ py: 4 }}>
                      No prompts found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedPrompts.map((prompt) => (
                  <React.Fragment key={prompt.id}>
                    <TableRow 
                      onClick={() => handleRowClick(prompt.id)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                        ...(expandedRow === prompt.id && { backgroundColor: 'rgba(25, 118, 210, 0.08)' })
                      }}
                    >
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(prompt.id);
                          }}
                        >
                          {expandedRow === prompt.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={prompt.name || 'N/A'} 
                          variant="outlined" 
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {prompt.panelId || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {prompt.modelId || 'N/A'}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDialog(prompt);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => handleDeleteClick(prompt, e)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                        <Collapse in={expandedRow === prompt.id} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              Prompt Details
                            </Typography>
                            
                            <Box sx={{ mb: 3 }}>
                              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2, mb: 2 }}>
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Document ID:
                                  </Typography>
                                  <Typography variant="body2">
                                    {prompt._id || prompt.id}
                                  </Typography>
                                </Box>
                                
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Name:
                                  </Typography>
                                  <Typography variant="body2">
                                    {prompt.name || 'N/A'}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Panel ID:
                                  </Typography>
                                  <Typography variant="body2">
                                    {prompt.panelId || 'N/A'}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Model ID:
                                  </Typography>
                                  <Typography variant="body2">
                                    {prompt.modelId || 'N/A'}
                                  </Typography>
                                </Box>
                              </Box>

                              {prompt.systemInstructions && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    System Instructions:
                                  </Typography>
                                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                    <Box sx={{ 
                                      '& p': { margin: '0.5em 0' },
                                      '& h1, & h2, & h3': { marginTop: '0.8em', marginBottom: '0.4em' },
                                      '& ul, & ol': { marginLeft: '1.5em', marginTop: '0.5em', marginBottom: '0.5em' },
                                      '& code': { backgroundColor: 'rgba(0,0,0,0.05)', padding: '0.2em 0.4em', borderRadius: '3px', fontFamily: 'monospace' },
                                      '& pre': { backgroundColor: 'rgba(0,0,0,0.05)', padding: '1em', borderRadius: '4px', overflow: 'auto' },
                                      '& pre code': { backgroundColor: 'transparent', padding: 0 }
                                    }}>
                                      <ReactMarkdown>{prompt.systemInstructions}</ReactMarkdown>
                                    </Box>
                                  </Paper>
                                </Box>
                              )}

                              {prompt.prompt && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Main Prompt:
                                  </Typography>
                                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                    <Box sx={{ 
                                      '& p': { margin: '0.5em 0' },
                                      '& h1, & h2, & h3': { marginTop: '0.8em', marginBottom: '0.4em' },
                                      '& ul, & ol': { marginLeft: '1.5em', marginTop: '0.5em', marginBottom: '0.5em' },
                                      '& code': { backgroundColor: 'rgba(0,0,0,0.05)', padding: '0.2em 0.4em', borderRadius: '3px', fontFamily: 'monospace' },
                                      '& pre': { backgroundColor: 'rgba(0,0,0,0.05)', padding: '1em', borderRadius: '4px', overflow: 'auto' },
                                      '& pre code': { backgroundColor: 'transparent', padding: 0 }
                                    }}>
                                      <ReactMarkdown>{prompt.prompt}</ReactMarkdown>
                                    </Box>
                                  </Paper>
                                </Box>
                              )}

                              {prompt.promptBeginner && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Beginner Prompt:
                                  </Typography>
                                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {prompt.promptBeginner}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}

                              {prompt.promptIntermediate && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Intermediate Prompt:
                                  </Typography>
                                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {prompt.promptIntermediate}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}

                              {prompt.mapLocations && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Map Locations:
                                  </Typography>
                                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {prompt.mapLocations}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}

                              {prompt.knowledgeBasePath && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Knowledge Base Path:
                                  </Typography>
                                  <Typography variant="body2">
                                    {prompt.knowledgeBasePath}
                                  </Typography>
                                </Box>
                              )}

                              {prompt.sourceType && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Source Type:
                                  </Typography>
                                  <Chip
                                    label={prompt.sourceType === 'knowledgebase' ? 'Knowledgebase' : 'Videos'}
                                    size="small"
                                    color={prompt.sourceType === 'knowledgebase' ? 'primary' : 'secondary'}
                                    variant="outlined"
                                  />
                                </Box>
                              )}

                              {prompt.vectorSearchQuery && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Vector Search Query:
                                  </Typography>
                                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {prompt.vectorSearchQuery}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}

                              {prompt.config && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Configuration:
                                  </Typography>
                                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1 }}>
                                    <Chip label={`Temperature: ${prompt.config.temp ?? 'N/A'}`} size="small" />
                                    <Chip label={`Top-P: ${prompt.config.topP ?? 'N/A'}`} size="small" />
                                    <Chip label={`Top-K: ${prompt.config.topK ?? 'N/A'}`} size="small" />
                                    <Chip label={`Tokens: ${prompt.config.tokens ?? 'N/A'}`} size="small" />
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Prompt Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingPrompt ? 'Edit Prompt' : 'Add New Prompt'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="ID"
              value={formData._id}
              onChange={(e) => handleFormChange('_id', e.target.value)}
              fullWidth
              required
              helperText="Unique identifier (e.g., DAY-BiometricsAdjs)"
            />

            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              fullWidth
              required
              helperText="Display name for the prompt"
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Panel ID"
                value={formData.panelId}
                onChange={(e) => handleFormChange('panelId', e.target.value)}
                fullWidth
                required
                helperText="Associated panel"
              />
              
              <TextField
                label="Model ID"
                value={formData.modelId}
                onChange={(e) => handleFormChange('modelId', e.target.value)}
                fullWidth
                required
                helperText="LLM model (e.g., gemini-2.5-flash-lite)"
              />
            </Box>

            <TextField
              label="System Instructions"
              value={formData.systemInstructions}
              onChange={(e) => handleFormChange('systemInstructions', e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText="System-level instructions for the AI"
            />

            <TextField
              label="Main Prompt"
              value={formData.prompt}
              onChange={(e) => handleFormChange('prompt', e.target.value)}
              fullWidth
              multiline
              rows={6}
              helperText="Primary prompt template"
            />

            <TextField
              label="Beginner Prompt"
              value={formData.promptBeginner}
              onChange={(e) => handleFormChange('promptBeginner', e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText="Optional beginner-level prompt variant"
            />

            <TextField
              label="Intermediate Prompt"
              value={formData.promptIntermediate}
              onChange={(e) => handleFormChange('promptIntermediate', e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText="Optional intermediate-level prompt variant"
            />

            <TextField
              label="Map Locations"
              value={formData.mapLocations}
              onChange={(e) => handleFormChange('mapLocations', e.target.value)}
              fullWidth
              multiline
              rows={2}
              helperText="Map location context"
            />

            <FormControl fullWidth>
              <InputLabel id="source-type-label">Source Type</InputLabel>
              <Select
                labelId="source-type-label"
                label="Source Type"
                value={formData.sourceType}
                onChange={(e) => {
                  handleFormChange('sourceType', e.target.value);
                  if (e.target.value !== 'knowledgebase') {
                    handleFormChange('knowledgeBasePath', '');
                  }
                }}
              >
                <MenuItem value="knowledgebase">Knowledgebase</MenuItem>
                <MenuItem value="videos">Videos</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="kb-path-label">Knowledge Base Path</InputLabel>
              <Select
                labelId="kb-path-label"
                label="Knowledge Base Path"
                value={formData.knowledgeBasePath}
                onChange={(e) => handleFormChange('knowledgeBasePath', e.target.value)}
                disabled={formData.sourceType !== 'knowledgebase' || kbFoldersLoading}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {kbFolders.map(folder => (
                  <MenuItem key={folder} value={folder}>{folder}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Vector Search Query"
              value={formData.vectorSearchQuery}
              onChange={(e) => handleFormChange('vectorSearchQuery', e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText="Query text used for vector similarity search against the knowledge base"
            />

            <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 'bold' }}>
              Configuration
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Temperature"
                type="number"
                inputProps={{ step: 0.1, min: 0, max: 2 }}
                value={formData.config.temp}
                onChange={(e) => handleFormChange('config.temp', e.target.value)}
                fullWidth
                helperText="Randomness (0-2)"
              />
              
              <TextField
                label="Top-P"
                type="number"
                inputProps={{ step: 0.1, min: 0, max: 1 }}
                value={formData.config.topP}
                onChange={(e) => handleFormChange('config.topP', e.target.value)}
                fullWidth
                helperText="Nucleus sampling (0-1)"
              />

              <TextField
                label="Top-K"
                type="number"
                inputProps={{ step: 1, min: 1 }}
                value={formData.config.topK}
                onChange={(e) => handleFormChange('config.topK', e.target.value)}
                fullWidth
                helperText="Top-K sampling"
              />

              <TextField
                label="Max Tokens"
                type="number"
                inputProps={{ step: 100, min: 1 }}
                value={formData.config.tokens}
                onChange={(e) => handleFormChange('config.tokens', e.target.value)}
                fullWidth
                helperText="Maximum output tokens"
              />
            </Box>

            {/* Test Metadata Editor */}
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Test Metadata
              </Typography>
              <TextField
                value={testMetadata}
                onChange={(e) => {
                  setTestMetadata(e.target.value);
                  if (testMetadataError) setTestMetadataError(false);
                }}
                onBlur={validateTestMetadata}
                fullWidth
                multiline
                rows={8}
                error={testMetadataError}
                helperText={testMetadataError ? 'Invalid JSON' : 'JSON body sent to the panel endpoint when testing'}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.82rem' } }}
              />
            </Box>

            {/* Test Results */}
            {testResult && (
              <Box sx={{ mt: 2 }}>
                {testResult.error ? (
                  <Alert severity="error">{testResult.error}</Alert>
                ) : (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      AI Response:
                    </Typography>
                    <Paper sx={{ p: 2, backgroundColor: 'grey.50', mb: 2 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {testResult.message}
                      </Typography>
                    </Paper>
                    {testResult.transactionDetails && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          Generated Prompt:
                        </Typography>
                        <Paper sx={{ p: 2, backgroundColor: 'grey.50', mb: 2, maxHeight: 200, overflow: 'auto' }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {testResult.transactionDetails.prompt}
                          </Typography>
                        </Paper>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          System Instructions:
                        </Typography>
                        <Paper sx={{ p: 2, backgroundColor: 'grey.50', mb: 2, maxHeight: 200, overflow: 'auto' }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {testResult.transactionDetails.systemInstructions}
                          </Typography>
                        </Paper>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Chip label={`Prompt Tokens: ${testResult.transactionDetails.promptTokenCount ?? 'N/A'}`} size="small" />
                          <Chip label={`Candidate Tokens: ${testResult.transactionDetails.candidatesTokenCount ?? 'N/A'}`} size="small" />
                          <Chip label={`Total Tokens: ${testResult.transactionDetails.totalTokenCount ?? 'N/A'}`} size="small" />
                          {testResult.transactionDetails.usageMetadata?.length > 0 && (() => {
                            const totalCost = testResult.transactionDetails.usageMetadata.reduce((sum, m) => sum + (m.totalCost || 0), 0);
                            return totalCost > 0 ? (
                              <Chip label={`Est. Cost: $${totalCost.toFixed(6)}`} size="small" color="warning" />
                            ) : null;
                          })()}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleTestPrompt}
            variant="outlined"
            color="secondary"
            disabled={testMetadataError || testLoading || !formData.panelId?.trim()}
            startIcon={testLoading ? <CircularProgress size={16} /> : null}
          >
            {testLoading ? 'Testing…' : 'Test'}
          </Button>
          <Button 
            onClick={handleSavePrompt} 
            variant="contained"
            disabled={!formData._id || !formData.name || !formData.panelId || !formData.modelId}
          >
            {editingPrompt ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the prompt "{promptToDelete?.name}"?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LLMPrompts;
